import classNames from 'classnames';
import { IconsEnum } from 'components/Icons';
import { SkeletonSportTable } from 'components/skeletonSportTable';
import { selectIsOpenCarrello } from 'features/carrello/carrelloSelectors';
import { miLive, miSport } from 'features/live/liveNav/utility';
import { SportManifestazioneFallback } from 'features/sport/components/sportManifestazioneFallback';
import { useScommessaStatus } from 'features/sport/hooks';
import { useGetLabelsByKeys } from 'hooks/useLingUI';
import useWindowSize from 'hooks/useWindowSize';
import { useTypedSelector } from 'lib/centralStore';
import { useRouter } from 'next/router';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { isServerSide } from 'utility/constant';
import { domContains } from 'utility/functions';
import { LoaderProps, ScommesseFallbackProps } from '.';
import styles from './ScommesseFallback.module.scss';

const Empty = () => <React.Fragment key={'live-disciplina-scommessa-fallback'} />;

export const Loader = ({ showOnScommessaLoading }: LoaderProps) => {
  const { isMobile } = useWindowSize();

  const { isUninitialized, isLoading } = useScommessaStatus();
  const isCarrelloOpen = useTypedSelector(selectIsOpenCarrello);

  if (isUninitialized || (isLoading && showOnScommessaLoading)) {
    return (
      <SkeletonSportTable className={classNames(styles.skeleton, { [styles.noMargin]: isMobile || isCarrelloOpen })} />
    );
  }

  return <Empty />;
};

export const EmptyLive = ({ container, hasFilter, hasRedirect, resetFilter }: ScommesseFallbackProps) => {
  const tmrRef = useRef<NodeJS.Timeout>();
  const [isHidden, setHidden] = useState(false);

  const { isReady, isFallback } = useRouter();
  const { isLoading, isUninitialized } = useScommessaStatus();
  const isCarrelloOpen = useTypedSelector(selectIsOpenCarrello);

  const [emptyMsg, filtersMsg, resetFiltriMsg, goToTopLive] = useGetLabelsByKeys([
    'empty-live-disciplina',
    'empty-filters-settings',
    'reset-filtri-scommesse',
    'cta-top-live',
  ]);

  const doCount = useCallback(() => {
    const nextSt = domContains(container, [
      "[data-kind='betting-option']",
      `[class*='${styles.skeleton}']`,
      "[class*='ScommesseAccordion_item']",
    ]);
    setHidden(nextSt);
  }, [container]);

  useLayoutEffect(() => {
    doCount();
    tmrRef.current = setInterval(doCount, 499);
    return () => {
      clearTimeout(tmrRef.current);
    };
  }, [doCount]);

  if (!isReady || isFallback || isServerSide || isHidden || isLoading || isUninitialized) {
    return <Empty />;
  }

  let button: any | undefined;
  if (hasFilter) {
    button = {
      text: resetFiltriMsg,
      action: resetFilter,
    };
  } else if (hasRedirect) {
    button = {
      link: miLive.slug,
      text: goToTopLive,
    };
  }
  return (
    <SportManifestazioneFallback
      text={hasFilter ? filtersMsg : emptyMsg}
      icon={hasFilter ? IconsEnum.SEARCH_OFF : undefined}
      button={button}
      haveRightMargin={isCarrelloOpen ? false : true}
    />
  );
};

export const EmptySport = ({ container, hasFilter, hasRedirect, resetFilter }: ScommesseFallbackProps) => {
  const tmrRef = useRef<NodeJS.Timeout>();
  const [isHidden, setHidden] = useState(false);

  const { isReady, isFallback } = useRouter();
  const { isLoading, isUninitialized } = useScommessaStatus();

  const isCarrelloOpen = useTypedSelector(selectIsOpenCarrello);

  const [emptyMsg, filtersMsg, resetFiltriMsg, goToTopSport] = useGetLabelsByKeys([
    'empty-sport-avvenimento',
    'empty-filters-settings',
    'reset-filtri-scommesse',
    'cta-top-sport',
  ]);

  const doCount = useCallback(() => {
    const nextSt = domContains(container, [
      "[data-kind='betting-option']",
      `[class*='${styles.skeleton}']`,
      "[class*='ScommesseAccordion_item']",
    ]);
    setHidden(nextSt);
  }, [container]);

  useLayoutEffect(() => {
    doCount();
    tmrRef.current = setInterval(doCount, 499);
    return () => {
      clearTimeout(tmrRef.current);
    };
  }, [doCount]);

  if (!isReady || isFallback || isServerSide || isHidden || isLoading || isUninitialized) {
    return <Empty />;
  }

  let button: any | undefined;
  if (hasFilter) {
    button = {
      text: resetFiltriMsg,
      action: resetFilter,
    };
  } else if (hasRedirect) {
    button = {
      link: miSport.slug,
      text: goToTopSport,
    };
  }

  return (
    <SportManifestazioneFallback
      text={hasFilter ? filtersMsg : emptyMsg}
      icon={hasFilter ? IconsEnum.SEARCH_OFF : undefined}
      button={button}
      haveRightMargin={isCarrelloOpen ? false : true}
    />
  );
};
